import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { CampaignQuoteTemplateSectionModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/campaignQuoteTemplateSection/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/campaignQuoteTemplateSection/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/campaignQuoteTemplateSection/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/campaignQuoteTemplateSection/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/campaignQuoteTemplateSection/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/campaignQuoteTemplateSection/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/campaignQuoteTemplateSection/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/campaignQuoteTemplateSection/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/campaignQuoteTemplateSection/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/campaignQuoteTemplateSection/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/campaignQuoteTemplateSection/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/campaignQuoteTemplateSection/DELETE_FAILURE'

export type CampaignQuoteTemplateSectionModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: CampaignQuoteTemplateSectionModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadCampaignQuoteTemplateSections(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().campaignQuoteTemplateSections.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/campaign_quote_template_sections.json', options)
      .then(({ data }: { data: CampaignQuoteTemplateSectionModel[] }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_TEMPLATE_SECTION_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadCampaignQuoteTemplateSection(campaignQuoteTemplateSectionId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.campaignQuoteTemplateSections.loadedForKeys]
    const loadedIds = [...state.campaignQuoteTemplateSections.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (campaignQuoteTemplateSectionId && !loadedIds.includes(campaignQuoteTemplateSectionId)){
      loadedIds.push(campaignQuoteTemplateSectionId)
    }

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/campaign_quote_template_sections/${campaignQuoteTemplateSectionId}.json`, options)
      .then(({ data }: { data: CampaignQuoteTemplateSectionModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_TEMPLATE_SECTION)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createCampaignQuoteTemplateSection(
  campaignQuoteTemplateSection: Partial<CampaignQuoteTemplateSectionModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      campaign_quote_template_section: campaignQuoteTemplateSection,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/campaign_quote_template_sections.json', options, config)
      .then(({ data }: { data: CampaignQuoteTemplateSectionModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_TEMPLATE_SECTION)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateCampaignQuoteTemplateSection(
  campaignQuoteTemplateSection: UpdateParams<CampaignQuoteTemplateSectionModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      campaign_quote_template_section: campaignQuoteTemplateSection,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/campaign_quote_template_sections/${campaignQuoteTemplateSection.id}.json`, options, config)
      .then(({ data }: { data: CampaignQuoteTemplateSectionModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_TEMPLATE_SECTION)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateQuoteTemplateSectionOrder(
  campaignQuoteTemplateId: number,
  sortedQuoteTemplateSectionIds: number[],
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      campaign_quote_template_id: campaignQuoteTemplateId,
      sorted_ids: sortedQuoteTemplateSectionIds,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api('/campaign_quote_template_sections/update_sort_order.json', options, config)
      .then(({ data }: { data: CampaignQuoteTemplateSectionModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_TEMPLATE_SECTION_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteCampaignQuoteTemplateSection(
  campaignQuoteTemplateSection: DeleteParams<CampaignQuoteTemplateSectionModel>,
) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/campaign_quote_template_sections/${campaignQuoteTemplateSection.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_TEMPLATE_SECTION)
        // @ts-expect-error TS2345
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: CampaignQuoteTemplateSectionModuleState = initialState,
  action: Action = { type: '' },
): CampaignQuoteTemplateSectionModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
